import api from './api';

/** Duration presets supported by create/extend APIs. */
export const DEMO_DURATION_PRESETS = [1, 3, 7, 15, 30] as const;
export type DemoDurationPresetDays = (typeof DEMO_DURATION_PRESETS)[number];

export type DemoListStatus = 'all' | 'active' | 'expired' | 'locked';

export interface SalesDemoRow {
  _id: string;
  companyName: string;
  slug: string;
  status?: string;
  isActive?: boolean;
  isDemo: boolean;
  country?: string;
  currency?: string;
  timezone?: string;
  subscriptionPlan?: string;
  demoStartsAt?: string;
  demoExpiresAt?: string;
  demoClientEmail?: string;
  demoVendorEmail?: string;
  demoKeyPrefix?: string;
  demoLockedAt?: string;
  demoSalesOwner?: string;
  demoNotes?: string;
  contactPerson?: string;
  phone?: string;
  branding?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    favicon?: string;
  };
  modules?: { key: string; enabled: boolean }[];
  createdAt?: string;
  remainingDays?: number;
  /** scheduled | active | expired — derived from dates; prefer over raw status for UI. */
  lifecycleStatus?: 'scheduled' | 'active' | 'expired';
  expired?: boolean;
  dateExpired?: boolean;
  notYetStarted?: boolean;
  seedStatus?: 'pending' | 'running' | 'completed' | 'failed';
  demoSeedStatus?: 'pending' | 'running' | 'completed' | 'failed';
  seedPercent?: number;
  demoSeedPercent?: number;
  demoSeedSteps?: string[];
  demoSeedTotalSteps?: number;
  demoSeedErrors?: string[];
}

export interface CreateSalesDemoPayload {
  companyName: string;
  clientEmail: string;
  clientName?: string;
  /** Alias for clientName — preferred contact label in Phase 1 UI */
  contactPerson?: string;
  phone?: string;
  days?: number;
  startsAt?: string;
  expiresAt?: string;
  enabledModules?: string[];
  country?: string;
  currency?: string;
  timezone?: string;
  subscriptionPlan?: string;
  salesOwner?: string;
  notes?: string;
  branding?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
  primaryColor?: string;
  logo?: string;
}

export type UpdateSalesDemoPayload = Partial<CreateSalesDemoPayload>;

export interface CreateSalesDemoResult {
  tenant: SalesDemoRow;
  demoKey: string;
  adminPassword: string;
  adminEmail?: string;
  vendorEmail?: string;
  vendorPassword?: string;
  startsAt?: string;
  expiresAt: string;
  days: number;
  demoUrl?: string;
  vendorUrl?: string;
  emailDelivered: boolean;
  emailMode: 'smtp' | 'log';
  emailReason?: string;
  smtpConfigured?: boolean;
  appLinks?: {
    user: string;
    vendor: string;
    partner: string;
    provider: string;
  };
  appleLinks?: {
    user: string;
    vendor: string;
    partner: string;
    provider: string;
  };
}

export interface ListSalesDemosParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: DemoListStatus;
  createdFrom?: string;
  createdTo?: string;
  startsFrom?: string;
  startsTo?: string;
  expiresFrom?: string;
  expiresTo?: string;
}

export interface ExtendSalesDemoPayload {
  days?: number;
  expiresAt?: string;
}

export interface ResendDemoInviteResult {
  tenantId: string;
  companyName?: string;
  slug?: string;
  demoKey: string;
  adminPassword: string;
  adminEmail?: string;
  vendorEmail?: string;
  vendorPassword?: string;
  expiresAt?: string;
  demoUrl?: string;
  vendorUrl?: string;
  emailDelivered: boolean;
  emailMode: 'smtp' | 'log';
  emailReason?: string;
  smtpConfigured?: boolean;
  appLinks?: {
    user: string;
    vendor: string;
    partner: string;
    provider: string;
  };
  appleLinks?: {
    user: string;
    vendor: string;
    partner: string;
    provider: string;
  };
}

export interface ResetDemoKeyResult {
  tenantId: string;
  demoKey: string;
  demoKeyPrefix: string;
}

export async function listSalesDemos(params: ListSalesDemosParams = {}) {
  const res = await api.get('/tenants/demos', { params });
  return res.data as { data: SalesDemoRow[]; total: number; page: number; limit: number };
}

export async function createSalesDemo(payload: CreateSalesDemoPayload) {
  const res = await api.post('/tenants/demos', payload);
  return res.data.data as CreateSalesDemoResult;
}

export async function updateSalesDemo(id: string, payload: UpdateSalesDemoPayload) {
  const res = await api.put(`/tenants/demos/${id}`, payload);
  return res.data.data as SalesDemoRow & {
    emailDelivered?: boolean;
    expiryUpdated?: boolean;
  };
}

export async function extendSalesDemo(id: string, payload: ExtendSalesDemoPayload | number = 7) {
  const body = typeof payload === 'number' ? { days: payload } : payload;
  const res = await api.post(`/tenants/demos/${id}/extend`, body);
  return res.data.data as SalesDemoRow & { emailDelivered?: boolean };
}

export async function revokeSalesDemo(id: string) {
  const res = await api.post(`/tenants/demos/${id}/revoke`);
  return res.data.data;
}

export async function deleteSalesDemo(id: string) {
  const res = await api.delete(`/tenants/demos/${id}`);
  return res.data.data as { _id: string; companyName?: string };
}

export async function resendDemoInvite(id: string) {
  const res = await api.post(`/tenants/demos/${id}/resend-invite`);
  return res.data.data as ResendDemoInviteResult;
}

export async function resetDemoKey(id: string) {
  const res = await api.post(`/tenants/demos/${id}/reset-key`);
  return res.data.data as ResetDemoKeyResult;
}

export async function redeemDemoKey(demoKey: string, userType = 'client_admin') {
  const res = await api.post('/auth/demo/redeem', { demoKey, userType });
  return res.data.data as {
    tenant: {
      _id: string;
      companyName: string;
      slug: string;
      branding?: { primaryColor?: string; secondaryColor?: string; logo?: string; favicon?: string };
      modules?: { key: string; enabled: boolean }[];
      currency?: string;
      isDemo?: boolean;
      demoStartsAt?: string;
      demoExpiresAt?: string;
    };
    user: Record<string, unknown>;
    accessToken: string;
    refreshToken: string;
    permissions?: string[];
    enabledFeatures?: string[];
  };
}
